<script setup lang="ts">
import { watch, onBeforeUnmount } from 'vue'

const props = defineProps<{
  /** Two-way bound open state. */
  modelValue: boolean
  /** Optional title rendered in the modal header. */
  title?: string
  /** Optional max-width override (e.g. "720px"). Defaults to the global 480px dialog width. */
  maxWidth?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

function close(): void {
  if (props.modelValue) {
    emit('update:modelValue', false)
  }
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    close()
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      document.addEventListener('keydown', onKeydown)
    } else {
      document.removeEventListener('keydown', onKeydown)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="modal-backdrop"
      @click.self="close"
    >
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        :style="maxWidth ? { maxWidth } : undefined"
      >
        <div
          v-if="title || $slots.header"
          class="modal-header"
        >
          <slot name="header">
            <h2 class="modal-title">{{ title }}</h2>
          </slot>
          <button
            type="button"
            class="modal-close"
            aria-label="Close"
            @click="close"
          >
            &times;
          </button>
        </div>

        <div class="modal-body">
          <slot />
        </div>

        <div
          v-if="$slots.footer"
          class="modal-footer"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>
